"use client";

import { Button } from "@universe/ui/components/button";
import { cn } from "@universe/ui/lib/utils";
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Check,
  ChevronRight,
  Copy,
  FileDown,
  Info,
  Loader2,
  Wallet,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import CopyButton from "@/components/copy-button";
import CurrencyFlag from "@/components/currency-flag";
import { NoTranslate } from "@/components/no-translate";
import useBankAccountValues from "@/hooks/useBankAccountValues";
import useDownloadSettlementInstructionPdf from "@/hooks/useDownloadSettlementInstructionsPdf";
import { useGetCountryName } from "@/hooks/useGetCountryName";
import {
  isPooledMethod,
  ownershipContext,
  type OwnershipContext,
} from "@/utils/settlementOwnership";
import { buildSettlementPdfExplainerKeys } from "@/utils/settlementPdfExplainers";
import { sortSettlementRails } from "@/utils/sortSettlementRails";
import { FiatSettlementMethod } from "@generated/types";

import { depositDetailsStyles as s } from "./deposit-details-styles";

// =============================================================================
// Types
// =============================================================================

interface FiatDepositDetailsProps {
  currency: string;
  settlementMethods: FiatSettlementMethod[];
}

interface FiatMethodCardProps {
  currency: string;
  settlementMethod: FiatSettlementMethod;
  /** Ownership context — non-null adds the ownership line to PDF + copy-all. */
  ownership?: OwnershipContext;
}

type MultiStep = "selection" | "detail";

// =============================================================================
// Helpers
// =============================================================================

/** Last 4 of account number, or IBAN when account number is absent. */
function accountEnding(
  bankAccount: FiatSettlementMethod["bankAccount"]
): string | null {
  const number = bankAccount.accountNumber?.replace(/\s/g, "");
  if (number && number.length >= 4) {
    return number.slice(-4);
  }
  const iban = bankAccount.iban?.replace(/\s/g, "");
  if (iban && iban.length >= 4) {
    return iban.slice(-4);
  }
  return null;
}

/**
 * Stacked label above rail pills. Shared by chooser cards and the detail
 * screen — darker label, pills wrap underneath.
 */
function AcceptedViaBlock({
  label,
  rails,
  acceptsSwift,
  noSwiftLabel,
}: {
  label: string;
  rails: { id: string; name: string }[];
  acceptsSwift: boolean;
  noSwiftLabel: string;
}) {
  return (
    <div className="space-y-1.5">
      <p className={s.acceptedViaLabel}>{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {rails.map((rail) => (
          <span
            key={rail.id}
            className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700"
          >
            {rail.name}
          </span>
        ))}
        {!acceptsSwift && (
          <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
            {noSwiftLabel}
          </span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Single Method Card
// =============================================================================

function FiatMethodCard({
  currency,
  settlementMethod,
  ownership = null,
}: FiatMethodCardProps) {
  const tBankAccount = useTranslations("bankAccount");
  const tCommon = useTranslations("common");
  const tDepositDetails = useTranslations("depositDetails");
  const getCountryName = useGetCountryName();

  const [copiedAll, setCopiedAll] = useState(false);

  const bankAccount = settlementMethod.bankAccount;
  const bankDetails = useBankAccountValues(bankAccount);
  const sortedRails = useMemo(
    () => sortSettlementRails(settlementMethod.rails),
    [settlementMethod.rails]
  );
  const acceptsSwift = sortedRails.some((r) => r.id === "SWIFT");

  // Build rails list for the PDF (blue badges + red "No SWIFT" if applicable)
  const pdfRails: { name: string; variant: "blue" | "red" }[] = [
    ...sortedRails.map((r) => ({
      name: r.name,
      variant: "blue" as const,
    })),
    ...(!acceptsSwift
      ? [
          {
            name: tDepositDetails("noSwiftWarning"),
            variant: "red" as const,
          },
        ]
      : []),
  ];

  // Transform bankDetails to use country names instead of codes for the PDF
  const bankDetailsForPdf = bankDetails.map((detail) => {
    if (detail.isCountry) {
      return {
        ...detail,
        value: getCountryName(detail.value) ?? detail.value,
      };
    }
    return detail;
  });

  // "Good to know" bullet lines for the PDF — a generic accepted-rails +
  // currency-only pair, one explainer per KNOWN rail, and the no-SWIFT
  // warning when applicable (key selection unit-tested in
  // utils/settlementPdfExplainers).
  const railNames = sortedRails.map((r) => r.name);
  const railNamesJoined =
    railNames.length > 1
      ? `${railNames.slice(0, -1).join(", ")} and ${railNames.at(-1)}`
      : (railNames[0] ?? "");
  // Key selection and order pinned in utils/settlementPdfExplainers.test.ts.
  // No sibling/one-account framing here: the PDF describes only the single
  // detail set the sender receives.
  const pdfExplainers = buildSettlementPdfExplainerKeys(
    sortedRails.map((r) => r.id),
    acceptsSwift
  )
    .filter((key) => key !== "acceptedRails" || railNames.length > 0)
    .map((key) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tDepositDetails(`pdfExplainers.${key}` as any, {
        rails: railNamesJoined,
        currency,
      })
    );

  // Ownership line for PDF subtitle + copy-all — same rule as the chooser:
  // only present when a pooled sibling exists (or the lone set is pooled).
  const ownershipLabel =
    ownership === "pooled"
      ? tDepositDetails("pooledAccountPdfSubtitle")
      : ownership === "personal"
        ? tDepositDetails("yourAccount")
        : undefined;

  const { isLoading: isPdfLoading, downloadSettlementInstructionPdf } =
    useDownloadSettlementInstructionPdf({
      type: "currencies",
      currency,
      title: tBankAccount("pdfTitle", { currency }),
      subtitle: ownershipLabel,
      rails: pdfRails,
      railsLabel: tDepositDetails("acceptedVia"),
      bankAccountFields: bankDetailsForPdf,
      explainers: pdfExplainers,
      explainersHeading: tDepositDetails("pdfExplainers.heading"),
      paymentReference: settlementMethod.reference,
      paymentReferenceLabel: settlementMethod.reference
        ? tDepositDetails("paymentReference")
        : undefined,
      paymentReferenceWarning: settlementMethod.reference
        ? tDepositDetails("referenceWarning")
        : undefined,
    });

  if (bankDetails.length === 0) {
    return (
      <div className="text-center text-muted-foreground">
        {tDepositDetails("noBankAccountDetails")}
      </div>
    );
  }

  const handleCopyAll = () => {
    const names = sortedRails.map((r) => r.name);
    if (!acceptsSwift) names.push(tDepositDetails("noSwiftWarning"));
    const lines = [
      ...(ownershipLabel ? [ownershipLabel] : []),
      `${tDepositDetails("acceptedVia")}: ${names.join(", ")}`,
      ...bankDetails
        .map((detail) => {
          if (detail.isCountry) {
            const countryName = getCountryName(detail.value);
            return `${detail.label}: ${countryName}`;
          }
          return `${detail.label}: ${detail.value}`;
        })
        .filter(Boolean),
    ];

    if (settlementMethod.reference) {
      lines.push(
        `${tDepositDetails("paymentReference")}: ${settlementMethod.reference}`
      );
    }

    navigator.clipboard.writeText(lines.join("\n"));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  return (
    <div className={s.methodBody}>
      <AcceptedViaBlock
        label={tDepositDetails("acceptedVia")}
        rails={sortedRails}
        acceptsSwift={acceptsSwift}
        noSwiftLabel={tDepositDetails("noSwiftWarning")}
      />

      {/* Payment reference — pooled accounts route by this reference, so lead with it */}
      {settlementMethod.reference && (
        <div className="space-y-2 rounded-lg border border-red-300 bg-red-50/[0.25] p-3">
          <div className="flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-600" />
            <span className="text-xs font-bold text-red-600">
              {tDepositDetails("referenceWarning")}
            </span>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className={s.fieldLabel}>
                {tDepositDetails("paymentReference")}
              </p>
              <p className={s.fieldValue}>
                <NoTranslate>{settlementMethod.reference}</NoTranslate>
              </p>
            </div>
            <CopyButton value={settlementMethod.reference} size="sm" />
          </div>
        </div>
      )}

      {/* Bank details grid */}
      <div className={s.fieldGrid}>
        {bankDetails.map(({ key, label, value, isCountry }) => {
          const copyValue = isCountry ? getCountryName(value) || value : value;
          return (
            <div key={key} className={s.fieldRow}>
              <div className="flex-1 min-w-0">
                <p className={s.fieldLabel}>{label}</p>
                {isCountry ? (
                  <div className="flex items-center gap-2 mt-1">
                    <CurrencyFlag currency={value} size="20px" />
                    <span className="text-sm md:text-base font-medium text-gray-900">
                      {getCountryName(value)}
                    </span>
                  </div>
                ) : (
                  <p className={s.fieldValue}>
                    <NoTranslate>{value}</NoTranslate>
                  </p>
                )}
              </div>

              <CopyButton value={copyValue} size="sm" />
            </div>
          );
        })}
      </div>

      {/* Action buttons */}
      <div className={s.actions}>
        <Button
          variant="outline"
          className={s.actionButton}
          onClick={handleCopyAll}
        >
          {copiedAll ? (
            <span className="inline-flex items-center justify-center">
              <Check className="h-4 w-4 mr-1 text-green-600" />
              <span>{tCommon("copied")}</span>
            </span>
          ) : (
            <span className="inline-flex items-center justify-center">
              <Copy className="h-4 w-4 mr-1" />
              <span>{tDepositDetails("copyAllDetails")}</span>
            </span>
          )}
        </Button>
        <Button
          variant="outline"
          className={s.actionButton}
          onClick={downloadSettlementInstructionPdf}
        >
          <span className="inline-flex items-center justify-center">
            {isPdfLoading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin text-brand-boundary" />
            ) : (
              <FileDown className="h-4 w-4 mr-1" />
            )}
            <span>{tCommon("downloadPDF")}</span>
          </span>
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Multi-method chooser row
// =============================================================================

function ChooserDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <span className="text-xs font-semibold text-muted-foreground shrink-0">
        {label}
      </span>
      <span className="text-sm font-semibold text-gray-900 truncate">
        <NoTranslate>{value}</NoTranslate>
      </span>
    </div>
  );
}

/**
 * "Your account" / "Pooled account" row. Rendered only when a pooled sibling
 * exists — when every set is personal, repeating "Your account" above each
 * card is what made one account read as several.
 */
function OwnershipLabelRow({ pooled }: { pooled: boolean }) {
  const tDepositDetails = useTranslations("depositDetails");

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {pooled ? (
        <Building2 className="h-4 w-4 text-brand-boundary shrink-0" />
      ) : (
        <Wallet className="h-4 w-4 text-brand-boundary shrink-0" />
      )}
      <span className="text-sm font-semibold text-gray-900">
        {pooled
          ? tDepositDetails("pooledAccount")
          : tDepositDetails("yourAccount")}
      </span>
      {pooled && (
        <span className="text-xs font-semibold text-red-600">
          {tDepositDetails("referenceRequired")}
        </span>
      )}
    </div>
  );
}

function SettlementMethodChooserCard({
  method,
  ownership,
  onSelect,
}: {
  method: FiatSettlementMethod;
  ownership: OwnershipContext;
  onSelect: () => void;
}) {
  const tDepositDetails = useTranslations("depositDetails");
  const tBankAccount = useTranslations("bankAccount");
  const pooled = isPooledMethod(method);
  const sortedRails = useMemo(
    () => sortSettlementRails(method.rails),
    [method.rails]
  );
  const acceptsSwift = sortedRails.some((r) => r.id === "SWIFT");
  const ending = accountEnding(method.bankAccount);
  const sortCode = method.bankAccount.nationalClearingCode;
  const iban = method.bankAccount.iban?.replace(/\s/g, "") || null;
  const country = method.bankAccount.country;
  // Currency product only — a EUR VIBAN can still be country GB / have a
  // clearing code, but the chooser must show IBAN for non-GBP products.
  const isGbp = method.currency === "GBP";
  const sortCodeLabel =
    tBankAccount(`fields.nationalClearingCode.${country}.label`) ||
    tBankAccount("fields.nationalClearingCode.default.label");
  const ibanLabel =
    tBankAccount(`fields.iban.${country}.label`) ||
    tBankAccount("fields.iban.default.label");

  // Bank identifiers only — rails + ending/sort code (GBP) or IBAN distinguish
  // the cards; account name is reserved for the detail screen.
  const summaryRows: { label: string; value: string }[] = [];
  if (pooled && method.reference) {
    summaryRows.push({
      label: tDepositDetails("paymentReference"),
      value: method.reference,
    });
  }
  if (isGbp) {
    if (ending) {
      summaryRows.push({
        label: tDepositDetails("accountEndingLabel"),
        value: ending,
      });
    }
    if (sortCode) {
      summaryRows.push({ label: sortCodeLabel, value: sortCode });
    }
  } else if (iban) {
    summaryRows.push({ label: ibanLabel, value: iban });
  } else if (ending) {
    summaryRows.push({
      label: tDepositDetails("accountEndingLabel"),
      value: ending,
    });
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`settlement-method-chooser-${method.id}`}
      className={cn(s.chooserCard, "items-start")}
    >
      <div className="flex-1 min-w-0 space-y-3">
        {ownership && <OwnershipLabelRow pooled={ownership === "pooled"} />}

        <AcceptedViaBlock
          label={tDepositDetails("acceptedVia")}
          rails={sortedRails}
          acceptsSwift={acceptsSwift}
          noSwiftLabel={tDepositDetails("noSwiftWarning")}
        />

        {summaryRows.length > 0 && (
          <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:gap-x-5 sm:gap-y-1.5">
            {summaryRows.map((row) => (
              <ChooserDetailRow
                key={`${row.label}-${row.value}`}
                label={row.label}
                value={row.value}
              />
            ))}
          </div>
        )}
      </div>
      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
    </button>
  );
}

// =============================================================================
// Main Component — handles single or multiple methods
// =============================================================================

export function FiatDepositDetails({
  currency,
  settlementMethods,
}: FiatDepositDetailsProps) {
  const tDepositDetails = useTranslations("depositDetails");
  const hasMultiple = settlementMethods.length > 1;
  const hasPooled = settlementMethods.some(isPooledMethod);
  // Ownership labels appear only when a pooled sibling exists (they would
  // differentiate nothing between all-personal sets) — with one exception in
  // ownershipContext: a lone pooled set is still labelled, because unlabelled
  // it reads as the customer's own account. Mirrors the statement PDF rule.

  const [step, setStep] = useState<MultiStep>("selection");
  const [selectedMethod, setSelectedMethod] =
    useState<FiatSettlementMethod | null>(null);

  // Reset chooser when the currency (or the method list) changes.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset selection when account/currency changes
    setStep("selection");
    setSelectedMethod(null);
  }, [currency, settlementMethods]);

  if (settlementMethods.length === 0) {
    return (
      <div className={cn(s.root, "text-center text-muted-foreground")}>
        {tDepositDetails("noBankAccountDetails")}
      </div>
    );
  }

  // Single method — same shell as multi-detail so spacing matches
  if (!hasMultiple) {
    const singleMethod = settlementMethods[0]!;
    const singleOwnership = ownershipContext(singleMethod, settlementMethods);
    return (
      <div className={s.root}>
        {singleOwnership && (
          <OwnershipLabelRow pooled={singleOwnership === "pooled"} />
        )}
        <FiatMethodCard
          currency={currency}
          settlementMethod={singleMethod}
          ownership={singleOwnership}
        />
      </div>
    );
  }

  if (step === "detail" && selectedMethod) {
    const detailOwnership = ownershipContext(selectedMethod, settlementMethods);
    // Same FiatMethodCard layout as a single-account currency — only the
    // outlined back control is added above it.
    return (
      <div className={s.root}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setStep("selection");
            setSelectedMethod(null);
          }}
          className={s.backButton}
          data-testid="change-bank-details"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{tDepositDetails("changeBankDetails")}</span>
        </Button>

        {/* Ownership echo — carries the chooser's differentiator onto the
            detail step (same conditional rule as the chooser cards) */}
        {detailOwnership && (
          <OwnershipLabelRow pooled={detailOwnership === "pooled"} />
        )}

        <FiatMethodCard
          currency={currency}
          settlementMethod={selectedMethod}
          ownership={detailOwnership}
        />
      </div>
    );
  }

  // Chooser — pick one bank-detail set
  return (
    <div className={s.root}>
      {/* One-account header card: the sets below are ways INTO one account,
          not separate accounts (CTO feedback, Aug 2026) */}
      <div
        className={cn(s.infoCallout, "items-start")}
        data-testid="multiple-accounts-explainer"
      >
        {/* mt-1 drops the icon to the cap height of the text-sm title, so its
            top lines up with the "Y" of "Your … account" */}
        <Info
          className="h-3.5 w-3.5 shrink-0 text-brand-boundary mt-1"
          strokeWidth={2.75}
        />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-gray-900">
            {tDepositDetails("oneAccountTitle", { currency })}
          </p>
          <p className="text-xs font-semibold text-gray-800">
            {tDepositDetails("oneAccountSubtitle", {
              count: settlementMethods.length,
            })}
          </p>
          {hasPooled && (
            <p className="text-xs font-semibold text-red-600">
              {tDepositDetails("pooledReferenceHint")}
            </p>
          )}
        </div>
      </div>

      {/* Decision heading: rails-led when the payment system is the only
          differentiator; ownership-led when a pooled set exists */}
      <p className={s.sectionTitle}>
        {tDepositDetails(
          hasPooled ? "selectBankDetailsPooled" : "selectBankDetails"
        )}
      </p>

      <div className={s.chooserList} data-testid="settlement-method-chooser">
        {settlementMethods.map((method) => (
          <SettlementMethodChooserCard
            key={method.id}
            method={method}
            ownership={ownershipContext(method, settlementMethods)}
            onSelect={() => {
              setSelectedMethod(method);
              setStep("detail");
            }}
          />
        ))}
      </div>
    </div>
  );
}
